/**
 * UnitStatusModal — Tier 0 slice-local component (SCRUM-167 epic).
 *
 * The status-edit modal for a property (Property IS the unit today;
 * when the BE grows a real Unit entity this same modal targets a
 * unit row). Implements the hard guard described in
 * `~/Desktop/design_handoff_properties/STATUS_MATRIX.md` §3 + §7:
 *
 *   - Two `<fieldset>` groups (Operational + Availability) each
 *     containing a pair of `<RadioCard>`s.
 *   - The Bookable radio is **disabled** whenever Inactive is selected.
 *     `disabled` lives on the DOM element (not just the visual), so
 *     keyboard activation is impossible (ACCESSIBILITY.md §10).
 *   - An inline `role="alert"` reads
 *     "Inactive + Bookable is not allowed…" whenever the user
 *     actively tried to reach the forbidden combo (i.e. Bookable
 *     was selected, then Inactive was chosen, leaving both checked
 *     briefly before the disable kicks in on next render).
 *   - The Save button is disabled in the forbidden state.
 *   - Esc closes (no commit).
 *
 * **Focus management** (SCRUM-167 Codex blocker 2 fix, 2026-05-28):
 *   - Initial focus lands on Cancel — Enter doesn't activate Save by
 *     accident (per ACCESSIBILITY.md §1 + WAI-ARIA AAA §3.9).
 *   - `useFocusTrap` cycles Tab / Shift+Tab inside the dialog so
 *     keyboard users can't tab behind the modal.
 *   - On close, focus returns to whatever owned focus when the
 *     dialog opened (the "Edit status" button in `PropertyHero`).
 *
 * The save flow calls `useUpdatePropertyStatus()` and handles
 * `conflict` (412/409 from optimistic lock) + `forbidden-state` (422)
 * with inline error states.
 */

import * as React from 'react';
import { Button } from '@sparta/ui-kit';
import { RadioCard } from './RadioCard.js';
import { useUpdatePropertyStatus } from '../api/use-update-property-status.js';
import { useFocusTrap } from '../../../shared/hooks/useFocusTrap.js';
import { useSrAnnouncement } from '../../../shared/hooks/useSrAnnouncement.js';
import type {
  PropertyView,
  UnitAvailability,
  UnitOperational,
} from '../api/types.js';

export interface UnitStatusModalProps {
  readonly property: PropertyView;
  readonly onClose: () => void;
}

export function UnitStatusModal({
  property,
  onClose,
}: UnitStatusModalProps): React.ReactElement {
  const [op, setOp] = React.useState<UnitOperational>(property.status.op);
  const [av, setAv] = React.useState<UnitAvailability>(
    property.status.av,
  );
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const { mutateAsync, status, errorKind, reset } = useUpdatePropertyStatus();
  const sr = useSrAnnouncement();

  // The forbidden combo: Inactive + Bookable. This drives:
  //   - the Bookable radio's `disabled` attribute
  //   - the inline `role="alert"` visibility
  //   - the Save button's `disabled` attribute
  // STATUS_MATRIX.md §3: "purely a safety net" — the radio's `disabled`
  // keeps the user from reaching this state via the keyboard or mouse;
  // the alert only fires if React renders the forbidden combo briefly
  // (e.g. mid-transition before a state update commits).
  const isForbidden = op === 'inactive' && av === 'bookable';

  // Initial focus on Cancel + Tab containment + focus return to the
  // opener on close. Per SCRUM-167 Codex blocker 2 — earlier passes
  // only set initial focus; the hook now ships the full WAI-ARIA AAA
  // §3.9 contract (initial / Tab cycle / Shift+Tab cycle / restore).
  useFocusTrap(dialogRef, { initialFocusRef: cancelRef });

  // Esc closes without committing. Separate from useFocusTrap because
  // the close behaviour is dialog-specific (Esc here means "no commit";
  // some dialogs may treat Esc as "save and close").
  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Reset the mutation error when the user adjusts a radio after
  // a failed save (so the inline error doesn't linger across
  // unrelated edits).
  const handleOpChange = (next: string): void => {
    if (errorKind !== null) reset();
    const nextOp = next as UnitOperational;
    setOp(nextOp);
    // When the user picks Inactive, eagerly auto-correct Bookable →
    // Non-bookable. Without this, the disabled radio leaves the form
    // in the forbidden state and the Save button stays disabled even
    // though the visible radio cards look consistent.
    if (nextOp === 'inactive' && av === 'bookable') {
      setAv('non-bookable');
    }
  };

  const handleAvChange = (next: string): void => {
    if (errorKind !== null) reset();
    setAv(next as UnitAvailability);
  };

  const handleSave = async (): Promise<void> => {
    if (isForbidden) return;
    try {
      await mutateAsync({
        propertyId: property.id,
        version: property.version,
        op,
        av,
      });
      // Per ACCESSIBILITY.md §6: "Status save → 'Status saved' (polite)".
      sr.polite('Status saved');
      onClose();
    } catch {
      // Error state is reflected via `status` + `errorKind`; the
      // modal stays open so the user can adjust + retry. The inline
      // `role="alert"` regions below already announce assertively
      // (conflict / forbidden / generic), so we don't double-announce
      // here.
    }
  };

  return (
    <div
      // Scrim overlay. `aria-hidden` is intentionally absent on the
      // backdrop — the dialog inside owns the modal semantics.
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{
        background: 'var(--bg-overlay)',
      }}
      onClick={(e) => {
        // Click on the scrim (but not the dialog) closes.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {sr.regions}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="unit-status-modal-title"
        aria-describedby="unit-status-modal-subtitle"
        className="w-full max-w-md rounded-2xl p-6"
        style={{
          background: 'var(--bg-surface)',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <h2
          id="unit-status-modal-title"
          className="text-lg font-semibold m-0"
          style={{ color: 'var(--fg-primary)' }}
        >
          Status — {property.name}
        </h2>
        <p
          id="unit-status-modal-subtitle"
          className="mt-1 mb-4 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          Operational and availability are independent. Inactive units
          cannot be Bookable.
        </p>

        <fieldset className="border-0 p-0 m-0 mb-4">
          <legend
            className="text-xs font-bold uppercase mb-2"
            style={{
              color: 'var(--fg-tertiary)',
              letterSpacing: '0.07em',
            }}
          >
            Operational
          </legend>
          <div className="flex flex-col gap-2">
            <RadioCard
              name="op"
              value="active"
              checked={op === 'active'}
              title="Active"
              sub="Billable and operational"
              onChange={handleOpChange}
            />
            <RadioCard
              name="op"
              value="inactive"
              checked={op === 'inactive'}
              title="Inactive"
              sub="Not billable. Hidden from new reservations."
              onChange={handleOpChange}
            />
          </div>
        </fieldset>

        <fieldset className="border-0 p-0 m-0 mb-4">
          <legend
            className="text-xs font-bold uppercase mb-2"
            style={{
              color: 'var(--fg-tertiary)',
              letterSpacing: '0.07em',
            }}
          >
            Availability
          </legend>
          <div className="flex flex-col gap-2">
            <RadioCard
              name="av"
              value="bookable"
              checked={av === 'bookable'}
              // Hard guard: disabled when op === 'inactive'.
              // STATUS_MATRIX.md §3: the disabled attribute (NOT just
              // visual styling) keeps the keyboard out.
              disabled={op === 'inactive'}
              title="Bookable"
              sub="Open on OTAs and website"
              badge={op === 'inactive' ? 'Not allowed' : undefined}
              aria-describedby={
                isForbidden ? 'unit-status-modal-alert' : undefined
              }
              onChange={handleAvChange}
            />
            <RadioCard
              name="av"
              value="non-bookable"
              checked={av === 'non-bookable'}
              title="Non-bookable"
              sub="Hidden from channels; reservations preserved"
              onChange={handleAvChange}
            />
          </div>
        </fieldset>

        {isForbidden ? (
          <div
            id="unit-status-modal-alert"
            role="alert"
            className="mb-4 rounded-lg p-3 text-sm"
            style={{
              background:
                'color-mix(in oklab, var(--danger-500) 12%, var(--bg-surface))',
              color: 'var(--fg-danger-on-tint)',
              border:
                '1px solid color-mix(in oklab, var(--danger-500) 30%, transparent)',
            }}
          >
            Inactive + Bookable is not allowed. Pick Inactive +
            Non-bookable, or set the unit back to Active.
          </div>
        ) : null}

        {errorKind === 'conflict' ? (
          <div
            role="alert"
            className="mb-4 rounded-lg p-3 text-sm"
            style={{
              background:
                'color-mix(in oklab, var(--warning-500) 14%, var(--bg-surface))',
              color: 'var(--fg-warning-on-tint)',
              border:
                '1px solid color-mix(in oklab, var(--warning-500) 30%, transparent)',
            }}
          >
            Saved elsewhere. Reload to see the latest, then try again.
          </div>
        ) : null}

        {errorKind === 'other' ? (
          <div
            role="alert"
            className="mb-4 rounded-lg p-3 text-sm"
            style={{
              background:
                'color-mix(in oklab, var(--danger-500) 12%, var(--bg-surface))',
              color: 'var(--fg-danger-on-tint)',
              border:
                '1px solid color-mix(in oklab, var(--danger-500) 30%, transparent)',
            }}
          >
            Couldn’t save status. Check your connection and try again.
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button
            ref={cancelRef}
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSave();
            }}
            disabled={isForbidden || status === 'pending'}
          >
            {status === 'pending' ? 'Saving…' : 'Save status'}
          </Button>
        </div>
      </div>
    </div>
  );
}
